'use client'

import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'
import { Button } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  slideFromLeftVariants,
  slideFromRightVariants,
  reducedMotionVariants,
  gridContainerVariants,
  listItemVariants,
} from '../../lib/motion'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'

/**
 * HeroSplitImage block props (split-image hero - content left, image right with outer frame)
 *
 * @remarks
 * **CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Image ONLY - no code window support
 * - Image extends beyond container on right side with outer frame
 * - Check icons (no circles) for feature bullets
 * - Supports primary/secondary button group above or below bullets
 * - Supports optional dark mode image with `darkSrc`
 * - Best for: hero sections showcasing product screenshots with feature highlights
 */
export interface HeroSplitImageProps {
  /**
   * Optional eyebrow rendered inside the hero content column, above the
   * heading and WITHIN the hero's background. Use for a small role/category
   * badge so it sits on the aurora/grid instead of floating on the bare
   * page background above the hero.
   */
  eyebrow?: ReactNode
  /** Main heading */
  title?: string
  /** Supporting subtitle */
  subtitle?: string
  /** Feature bullet items with check icons */
  features?: string[]
  /** Primary action button */
  primaryButton?: { text: string; url: string }
  /** Secondary action button */
  secondaryButton?: { text: string; url: string }
  /** Button position relative to features list */
  buttonPosition?: 'above' | 'below'
  /** Image to display (optional darkSrc for dark mode) */
  image?: {
    src: string
    alt: string
    /** Optional dark mode image - if provided, swaps automatically */
    darkSrc?: string
  }
  /** Optional background pattern (e.g., GradientBlur, GridDashed, etc.) */
  background?: ReactNode
  /**
   * When true, extends the hero section behind the sticky navbar.
   * Uses negative margin to pull section up, with padding to keep content visible.
   * @default true
   */
  behindNavbar?: boolean
  className?: string
}

const HeroSplitImage = ({
  title = 'Build Beautiful Interfaces with MDXUI',
  subtitle = 'Type-safe components for building Sites and Apps with MDX. Production-ready and fully accessible.',
  features = [
    'Type-safe components',
    'Dark mode support',
    'Fully accessible',
    'Production ready',
  ],
  primaryButton,
  secondaryButton,
  buttonPosition = 'above',
  eyebrow,
  // No default: a split-image hero must only render an image the caller
  // actually supplied. A baked-in placeholder src 404s on any consumer that
  // doesn't host that asset (the live priya.studio.sb / cfo.hq.sb broken
  // heroes), and violates the do-design-agency rule "never use an image hero
  // without a real asset". When omitted, the hero renders image-free
  // (single-column) instead.
  image,
  background,
  behindNavbar = true,
  className,
}: HeroSplitImageProps) => {
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion ? reducedMotionVariants : slideFromLeftVariants
  const imageAnimation = prefersReducedMotion ? reducedMotionVariants : slideFromRightVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : listItemVariants

  return (
    <div className={cn(
      'relative overflow-hidden',
      behindNavbar && BEHIND_NAVBAR_CLASSES,
      className
    )}>
      {background && (
        <div className="absolute inset-0">{background}</div>
      )}
      <motion.section
        className="relative z-10 overflow-x-hidden py-16 sm:py-24 lg:py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={cn('grid gap-10', image && 'lg:grid-cols-2 lg:items-center')}>
            {/* Content - Left. Constrained when image-free so line length stays
                readable (no 1280px-wide headings). */}
            <motion.div className={cn('w-full min-w-0', image ? 'max-w-full' : 'max-w-3xl')} variants={contentAnimation}>
              {eyebrow && (
                <div className="mb-5">{eyebrow}</div>
              )}
              <h1 className="mb-5 font-semibold text-4xl lg:text-5xl">
                {title}
              </h1>
              <Markdown size="base" className={cn(buttonPosition === 'above' ? 'mb-8' : 'mb-8 lg:mb-12')}>
                {subtitle}
              </Markdown>
              {/* Button group - above */}
              {buttonPosition === 'above' && (primaryButton || secondaryButton) && (
                <motion.div
                  className="mb-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mb-12"
                  variants={itemAnimation}
                >
                  {primaryButton && (
                    <Button asChild className="w-full sm:w-auto">
                      <a href={primaryButton.url}>{primaryButton.text}</a>
                    </Button>
                  )}
                  {secondaryButton && (
                    <Button variant="outline" asChild className="w-full sm:w-auto">
                      <a href={secondaryButton.url}>{secondaryButton.text}</a>
                    </Button>
                  )}
                </motion.div>
              )}
              <motion.ul
                className="grid max-w-xl gap-4 sm:grid-cols-2"
                variants={gridContainerVariants(0.05, 0.1)}
              >
                {features.map((feature, index) => (
                  <motion.li
                    key={String(index)}
                    className="flex min-w-0 items-center gap-2"
                    variants={itemAnimation}
                  >
                    <Check className="size-5 shrink-0 text-primary" />
                    <p className="break-words text-sm font-medium">{feature}</p>
                  </motion.li>
                ))}
              </motion.ul>
              {/* Button group - below */}
              {buttonPosition === 'below' && (primaryButton || secondaryButton) && (
                <motion.div
                  className="mt-8 flex max-w-full flex-col gap-3 sm:flex-row lg:mt-12"
                  variants={itemAnimation}
                >
                  {primaryButton && (
                    <Button asChild className="w-full sm:w-auto">
                      <a href={primaryButton.url}>{primaryButton.text}</a>
                    </Button>
                  )}
                  {secondaryButton && (
                    <Button variant="outline" asChild className="w-full sm:w-auto">
                      <a href={secondaryButton.url}>{secondaryButton.text}</a>
                    </Button>
                  )}
                </motion.div>
              )}
            </motion.div>

            {/* Image - Right with outer frame, extends beyond container. Only
                rendered when a real image asset was supplied (see prop note). */}
            {image && (
            <motion.div className="relative min-w-0 overflow-visible" variants={imageAnimation}>
              <div className="w-[120%] rounded-l-[var(--radius-2xl,calc(var(--radius)*2))] border-y border-l border-border bg-muted/50 py-1 pl-1 shadow-xl lg:w-[160%]">
                {image.darkSrc ? (
                  <>
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="aspect-video w-full rounded-l-[var(--radius-xl,calc(var(--radius)*1.5))] object-cover object-left dark:hidden"
                    />
                    <img
                      src={image.darkSrc}
                      alt={image.alt}
                      className="hidden aspect-video w-full rounded-l-[var(--radius-xl,calc(var(--radius)*1.5))] object-cover object-left dark:block"
                    />
                  </>
                ) : (
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="aspect-video w-full rounded-l-[var(--radius-xl,calc(var(--radius)*1.5))] object-cover object-left"
                  />
                )}
              </div>
            </motion.div>
            )}
          </div>
        </div>
      </motion.section>
    </div>
  )
}

export { HeroSplitImage }
